/**
 * Pure date/slot resolution for Flexible Programs.
 *
 * A Flexible Program is a cycle with `storeType: "flexible"` owned by the
 * client and authored by their coach. Its `programs[]` are AUTHORED WEEKS; the
 * last one re-issues itself every 7 days - while its own `repeatActiveWeek` is
 * on - until the coach authors another. Each re-issue is an OCCURRENCE: a 7-day
 * window that reads through to the authored week (no copy) and owns its own
 * claim ledger in `cycle.occurrences[]`.
 *
 * Two invariants this module depends on:
 *
 *  1. THE LATTICE. Every authored `startDate` sits on
 *     `programs[0].startDate + 7k`. Occurrence windows are derived from the
 *     authored anchor, never chained off the previous window, so a new
 *     authored week always takes over on an occurrence boundary and toggling
 *     `repeatActiveWeek` off and on never shifts the lattice.
 *
 *  2. NEVER SUBTRACT YYYYMMDD INTEGERS. `20251101 - 20251031` is 70, not 1.
 *     All arithmetic here round-trips through moment, in UTC, so the result
 *     does not depend on the browser's timezone.
 *
 * This file is mirrored, function for function, in:
 *   msb-api/lib/api_helpers/common/flexible-helpers.js
 *   msb-mobile/src/utils/flexibleProgram.ts
 * Change one, change all three, and keep the test tables in step. The API copy
 * carries the authoritative table (run: node lib/api_helpers/common/flexible-helpers.test.js).
 */
import moment from "moment";

const YMD = "YYYYMMDD";

export const FLEXIBLE_STORE_TYPE = "flexible";
export const SPLIT_SUGGESTED = "suggested";
export const SPLIT_FORCED = "forced";
export const VALID_SPLIT_TYPES = [SPLIT_SUGGESTED, SPLIT_FORCED];

/** Safety valve for the occurrence walk - ~20 years of weeks. */
const MAX_OCCURRENCE_STEPS = 1040;

// --- YYYYMMDD arithmetic -----------------------------------------------

function toMoment(yyyymmdd) {
  return moment.utc(String(yyyymmdd), YMD).startOf("day");
}

function toNumber(value) {
  return Number(value.format(YMD));
}

export function addDays(yyyymmdd, days) {
  return toNumber(toMoment(yyyymmdd).add(days, "days"));
}

/** Calendar days from `from` to `to`; negative when `to` is earlier. */
export function daysBetween(from, to) {
  return toMoment(to).diff(toMoment(from), "days");
}

/** Normalise any integer to a 0-6 weekday, including negatives. */
function normalizeWeekday(value) {
  return (((Number(value) || 0) % 7) + 7) % 7;
}

// --- Authored weeks ----------------------------------------------------

/** Non-deleted, dated authored weeks, ascending by startDate. */
export function authoredWeeks(cycle) {
  return (cycle?.programs || [])
    .filter((program) => program && !program.isDeleted && program.startDate)
    .slice()
    .sort((a, b) => Number(a.startDate) - Number(b.startDate));
}

/**
 * The authored week right before the one with `order`, by start date. Orders
 * are not contiguous (deleted and replaced weeks keep theirs), so `order - 1`
 * can point at a week that no longer exists. Null for the first week.
 */
export function previousAuthoredWeek(cycle, order) {
  const weeks = authoredWeeks(cycle);
  const index = weeks.findIndex((week) => Number(week.order) === Number(order));
  return index > 0 ? weeks[index - 1] : null;
}

export function isFlexibleCycle(cycle) {
  return cycle?.storeType === FLEXIBLE_STORE_TYPE;
}

/**
 * Effective split mode: the week's own value, else the cycle's, else
 * "suggested" for every storeType.
 *
 * Anything not in VALID_SPLIT_TYPES falls through to the default rather than
 * being passed on. Only "forced" ever restricts which date a slot may land on,
 * so an unrecognised value reaching a caller would silently read as unforced
 * anyway - this just makes that explicit and keeps a stale value out of the UI,
 * where it would match none of the options.
 */
export function resolveSplitType(cycle, program) {
  const value = program?.trainingSplitType || cycle?.trainingSplitType;
  if (VALID_SPLIT_TYPES.includes(value)) return value;
  return SPLIT_SUGGESTED;
}

// --- Occurrence resolution ---------------------------------------------

/** Which authored week and which of its occurrences covers `dateYyyymmdd`. */
export function resolveFlexibleOccurrence(cycle, dateYyyymmdd) {
  const weeks = authoredWeeks(cycle);
  if (!weeks.length || !dateYyyymmdd) return null;

  const date = Number(dateYyyymmdd);
  const first = weeks[0];

  // Before the program starts: surface the first week so the coach can be
  // shown what is coming, with that week's own (index 0) window.
  if (date < Number(first.startDate)) {
    return {
      status: "beforeStart",
      program: first,
      programOrder: Number(first.order),
      authoredStartDate: Number(first.startDate),
      occurrenceIndex: 0,
      occurrenceStartDate: Number(first.startDate),
      occurrenceEndDate: addDays(first.startDate, 6),
      isRepeat: false,
      truncated: false,
      nextAuthoredStartDate: weeks[1] ? Number(weeks[1].startDate) : null,
    };
  }

  // The live week is the LATEST authored week that has started. A week keeps
  // re-issuing until the next authored one starts - including a week that is
  // not the last, when the coach has queued one further out (the "ongoing
  // range recomputes to end right before it" case).
  let liveIndex = 0;
  for (let i = 0; i < weeks.length; i++) {
    if (Number(weeks[i].startDate) <= date) liveIndex = i;
    else break;
  }
  const live = weeks[liveIndex];
  const next = weeks[liveIndex + 1] || null;
  const nextAuthoredStartDate = next ? Number(next.startDate) : null;

  const occurrenceIndex = Math.floor(daysBetween(live.startDate, date) / 7);
  // The flag belongs to the WEEK, and only the live one's matters: once a newer
  // week starts, an older week's flag stops governing entirely.
  const repeats = live?.repeatActiveWeek !== false;

  // Repeat off and past the authored week's own window: nothing is live. Report
  // the one window that ever existed, so the coach UI can say "ran Sep 1 - 7".
  if (!repeats && occurrenceIndex > 0) {
    return {
      status: "ended",
      program: live,
      programOrder: Number(live.order),
      authoredStartDate: Number(live.startDate),
      occurrenceIndex: 0,
      occurrenceStartDate: Number(live.startDate),
      occurrenceEndDate: addDays(live.startDate, 6),
      isRepeat: false,
      truncated: false,
      nextAuthoredStartDate,
    };
  }

  const occurrenceStartDate = addDays(live.startDate, occurrenceIndex * 7);
  let occurrenceEndDate = addDays(occurrenceStartDate, 6);
  let truncated = false;

  // A no-op while the lattice invariant holds; defence against legacy or
  // hand-edited data where a queued week does not land on a boundary.
  if (nextAuthoredStartDate && occurrenceEndDate >= nextAuthoredStartDate) {
    occurrenceEndDate = addDays(nextAuthoredStartDate, -1);
    truncated = true;
  }

  return {
    status: "active",
    program: live,
    programOrder: Number(live.order),
    authoredStartDate: Number(live.startDate),
    occurrenceIndex,
    occurrenceStartDate,
    occurrenceEndDate,
    isRepeat: occurrenceIndex > 0,
    truncated,
    nextAuthoredStartDate,
  };
}

// --- Claim ledger ------------------------------------------------------

export function findOccurrenceRecord(cycle, occurrenceStartDate) {
  return (
    (cycle?.occurrences || []).find(
      (occurrence) =>
        Number(occurrence?.startDate) === Number(occurrenceStartDate)
    ) || null
  );
}

/**
 * Claim state for a resolved occurrence. An occurrence with no stored record
 * is simply unclaimed - rows are written lazily on first claim.
 */
export function resolveFlexibleClaims(cycle, resolved) {
  const record = resolved
    ? findOccurrenceRecord(cycle, resolved.occurrenceStartDate)
    : null;
  const claims = (record?.claims || [])
    .slice()
    .sort((a, b) => Number(a.order) - Number(b.order));
  const sessions = resolved?.program?.sessions || [];

  return {
    claims,
    claimedOrders: new Set(claims.map((claim) => Number(claim.order))),
    claimedCount: claims.length,
    total: sessions.length,
    isComplete: sessions.length > 0 && claims.length >= sessions.length,
    daysTrained: claims
      .map((claim) => Number(claim.date))
      .sort((a, b) => a - b),
  };
}

// --- Slot placement ----------------------------------------------------

/** The date a forced slot is pinned to inside a given occurrence. */
export function forcedSlotDate(
  occurrenceStartDate,
  workoutDay,
  firstDayOfWeek
) {
  const offset = normalizeWeekday(
    normalizeWeekday(workoutDay) - normalizeWeekday(firstDayOfWeek)
  );
  return addDays(occurrenceStartDate, offset);
}

/**
 * May `slotOrder` be claimed onto `dateYyyymmdd`?
 *
 * Open and suggested allow any date inside the occurrence window and any
 * order (D3 before D1 is fine). Forced pins the slot to its weekday. Claims
 * are deliberately confined to the window: a free-range date would make
 * "which occurrence does this belong to" ambiguous and the completion ratio
 * meaningless. Re-dating an existing claim is a separate move action.
 */
export function isFlexibleSlotAvailable(
  cycle,
  resolved,
  slotOrder,
  dateYyyymmdd
) {
  if (!resolved || resolved.status !== "active") return false;

  const order = Number(slotOrder);
  const session = (resolved.program?.sessions || []).find(
    (candidate) => Number(candidate.order) === order
  );
  if (!session) return false;

  const date = Number(dateYyyymmdd);
  if (date < resolved.occurrenceStartDate) return false;
  if (date > resolved.occurrenceEndDate) return false;

  if (resolveFlexibleClaims(cycle, resolved).claimedOrders.has(order)) {
    return false;
  }

  if (resolveSplitType(cycle, resolved.program) === SPLIT_FORCED) {
    return (
      date ===
      forcedSlotDate(
        resolved.occurrenceStartDate,
        session.workoutDay,
        cycle?.firstDayOfWeek
      )
    );
  }

  return true;
}

// --- Walking the lattice -----------------------------------------------

/**
 * Every occurrence window overlapping [fromYyyymmdd, toYyyymmdd], with its
 * claim state. Windows are derived, not stored, so a week the client skipped
 * entirely still appears (as 0 of N) - which is what the coach's History tab
 * needs.
 */
export function listFlexibleOccurrences(cycle, options) {
  const weeks = authoredWeeks(cycle);
  const to = Number(options?.toYyyymmdd);
  if (!weeks.length || !to) return [];

  const anchor = Number(weeks[0].startDate);
  const from = Number(options?.fromYyyymmdd) || anchor;

  const out = [];
  let cursor = Math.max(from, anchor);
  let steps = 0;

  while (cursor <= to && steps++ < MAX_OCCURRENCE_STEPS) {
    const resolved = resolveFlexibleOccurrence(cycle, cursor);
    if (!resolved || resolved.status !== "active") break;

    out.push({ ...resolved, ...resolveFlexibleClaims(cycle, resolved) });
    cursor = addDays(resolved.occurrenceEndDate, 1);
  }

  return out;
}

/** The lattice-aligned occurrence start containing `dateYyyymmdd`. */
export function latticeWeekStart(cycle, dateYyyymmdd) {
  const weeks = authoredWeeks(cycle);
  if (!weeks.length) return null;

  const anchor = Number(weeks[0].startDate);
  const date = Number(dateYyyymmdd);
  if (date < anchor) return anchor;

  return addDays(anchor, Math.floor(daysBetween(anchor, date) / 7) * 7);
}

/**
 * Where the coach's next authored week starts: the lattice boundary after the
 * occurrence containing today, pushed past any already-authored week.
 */
export function nextFlexibleWeekStartDate(cycle, todayYyyymmdd) {
  const weeks = authoredWeeks(cycle);
  if (!weeks.length) return null;

  const anchor = Number(weeks[0].startDate);
  const today = Number(todayYyyymmdd);
  let candidate =
    today < anchor ? anchor : addDays(latticeWeekStart(cycle, today), 7);

  const last = Number(weeks[weeks.length - 1].startDate);
  while (candidate <= last) candidate = addDays(candidate, 7);

  return candidate;
}

/** Snap an arbitrary requested start date onto the lattice (rounding down). */
export function snapToLattice(cycle, dateYyyymmdd) {
  return latticeWeekStart(cycle, dateYyyymmdd);
}

/**
 * Start date for the FIRST authored week of a brand-new program: the next
 * `firstDayOfWeek` strictly after today, so always 1-7 days out.
 */
export function firstFlexibleWeekStartDate(firstDayOfWeek, todayYyyymmdd) {
  const today = toMoment(todayYyyymmdd);
  const offset = normalizeWeekday(
    normalizeWeekday(firstDayOfWeek) - today.day()
  );
  return toNumber(today.add(offset === 0 ? 7 : offset, "days"));
}

/**
 * The date range the Program Manager prints for an authored week: it ends the
 * day before the next authored week, or runs "ongoing" when none is queued.
 */
export function authoredWeekRange(cycle, programOrder) {
  const weeks = authoredWeeks(cycle);
  const index = weeks.findIndex(
    (week) => Number(week.order) === Number(programOrder)
  );
  if (index === -1) return null;

  const week = weeks[index];
  const next = weeks[index + 1] || null;
  const startDate = Number(week.startDate);

  if (week?.repeatActiveWeek === false) {
    return { startDate, endDate: addDays(startDate, 6), ongoing: false };
  }
  if (next) {
    return { startDate, endDate: addDays(next.startDate, -1), ongoing: false };
  }
  return { startDate, endDate: null, ongoing: true };
}
